/**
 * ***************************************************************
 * WINDS ALOFT UTILITIES (CLIENT SIDE)
 * ***************************************************************
 *
 * PURPOSE:
 * - Fetch winds aloft text from weatherServer (Vercel)
 * - Parse FAA coded winds into usable values
 * - Map results into NavLog table format
 *
 * ***************************************************************
 */

/**
 * ---------------------------------------------------------------
 * BASE URL FOR WEATHER SERVER (VERCEL DEPLOYMENT)
 * ---------------------------------------------------------------
 * This is the deployed weather server on Vercel.
 * All fetch requests go through this URL.
 */
const API_BASE = "https://weatherserver-sand.vercel.app";

/**
 * ---------------------------------------------------------------
 * FETCH WINDS ALOFT TEXT (BY REGION)
 * ---------------------------------------------------------------
 *
 * region examples:
 *   mia, bos, chi, dfw, slc, sfo
 *
 * fcst examples:
 *   6, 12, 24
 *
 * OUTPUT:
 *   {
 *     regionInput,
 *     region,
 *     area,
 *     rawText   <-- FAA winds aloft text block
 *   }
 *
 * NOTES:
 * - Uses Vercel serverless API
 * - Returns null if fetch fails
 */
export const fetchWindsRegionText = async (region = "mia", fcst="6") => {
    const url =
    `${API_BASE}/api/windsaloft/${region}` +
    `?fcst=${encodeURIComponent(fcst)}`;


  try {
    console.log("Fetching winds URL:", url);

const res = await fetch(url, {
      method: "GET",
      mode: "cors",
    });

    console.log("Winds response status:", res.status, "ok:", res.ok);

    if (!res.ok) {
      const text = await res.text();
      console.error("Bad response body:", text);
      throw new Error(`HTTP ${res.status}`);
    }

    const data = await res.json();
    console.log("Winds JSON data:", data);

    return data;
  } catch (err) {
    console.error("Winds fetch failed:", err);
    console.error("Fetch URL that failed:", url);
    return null;
  }
};

/**
 * Decode one FAA coded winds/temp group.
 * Examples:
 *   0511
 *   0511+07
 *   0417+00
 */
const decodeGroup = (group, altitude) => {
  const s = String(group || "").trim();
  if (!s || s.length < 4) return null;
  const tempPart = s.slice(4);
  let temp = "";
  if (tempPart) {
    const isNegative = tempPart.includes("-");
    const clean = tempPart.replace("+", "").replace("-", "");
    temp = (isNegative ? -1 : 1) * Number(clean);
  }
  console.log("decodeGroup group =", group);
  // check for 9900 light and variable winds
  // this may include temperature
  if (
    s === "9900" ||
    s.startsWith("9900+") ||
    s.startsWith("9900-") ||
    s === "990" ||
    s.startsWith("990+") ||
    s.startsWith("990-") ||
    s === "99" ||
    s.startsWith("99+") ||
    s.startsWith("99-") 
    
  ) {
    return { 
      dir: 0, 
      spd: 0, 
      temp: temp || ""
    };
  }
  let dd = Number(s.slice(0, 2));
  let ff = Number(s.slice(2, 4));
  

  // FAA winds aloft rule for 100+ kt winds
  if (dd >= 51 && dd <= 86) {
    dd = dd - 50;
    ff = ff + 100;
  }

  const dir = dd * 10;
  const spd = ff;

  

  // 3000 ft usually has no temperature
  if (Number(altitude) === 3000 && !tempPart) {
    temp = "";
  }

  return { dir, spd, temp };
};

/**
 * Parse one station row from FAA winds aloft raw text.
 */
export const parseStationLine = (rawText, stationId) => {
  const lines = String(rawText || "").split(/\r?\n/);
  const id = String(stationId || "").trim().toUpperCase();

  const line = lines.find((ln) => ln.trim().startsWith(id + " "));
  if (!line) {
    console.warn("Station not found in winds text:", id);
    return null;
  }

  const parts = line.trim().split(/\s+/);
  if (parts.length < 4) {
    console.warn("Invalid station line:", line);
    return null;
  }

  const g3000 = parts[1];
  const g6000 = parts[2];
  const g9000 = parts[3];

  const p1 = decodeGroup(g3000, 3000);
  const p2 = decodeGroup(g6000, 6000);
  const p3 = decodeGroup(g9000, 9000);

  return {
    alt1: "3000",
    alt2: "6000",
    alt3: "9000",

    wd1: p1?.dir ?? "",
    ws1: p1?.spd ?? "",
    t1: p1?.temp ?? "",

    wd2: p2?.dir ?? "",
    ws2: p2?.spd ?? "",
    t2: p2?.temp ?? "",

    wd3: p3?.dir ?? "",
    ws3: p3?.spd ?? "",
    t3: p3?.temp ?? "",
  };
};